import { SupportedLocale } from '@src/cnst/translations.cnst'
import { campaignContext } from '@src/context/campaign.context'
import { languageContext } from '@src/context/language.context'
import { enhanceSignupUrl, getSignupURL } from '@src/helpers/signupUrl'
import { useAppSelector } from '@src/hooks/useAppSelector'
import { useContext, useMemo } from 'react'

export function useSignupHref(
  codeProp?: string,
  ignoreConfirmationProp?: boolean,
): {
  href: string
} {
  const campaign = useContext(campaignContext)
  const languageContextLocale = useContext(languageContext).locale
  const discountCode = useAppSelector(state => state.discountCode.code)
  const savedDiscountCode =
    typeof window !== 'undefined'
      ? (window.sessionStorage.getItem('discountCode') ?? undefined)
      : undefined
  const popupCode = discountCode || savedDiscountCode
  const reduxReferral = useAppSelector(state => state.discountCode.referral)
  const reduxName = useAppSelector(state => state.discountCode.name)

  const locale = useMemo(() => {
    return getFirstDefinedElement<SupportedLocale | undefined>([
      // The campaign locale has a higher priority than the LanguageContext locale
      // for pages such as /c/plan-offer-uk which whose filename ends in en-us.tsx
      // but which still needs the en-GB locale in its signup links.
      campaign?.locale,
      // Fall back to the general-purpose site-wide LanguageContext locale for
      // 99% of signup buttons.
      languageContextLocale,
    ])
  }, [campaign, languageContextLocale])

  const code = useMemo(
    () =>
      getFirstDefinedElement<string | undefined>([
        // The code from the props gets the highest priority so that landing pages
        // with multiple codes such as /c/choose-your-offer can specify a different
        // code on one specific signup button.
        codeProp,
        // Fall back to the CampaignContext code for the majority of landing page
        // signup buttons.
        campaign?.code,
        // Last priority is checking for coupon codes stored in the global state or the session storage coming from popups
        popupCode,
      ]),
    [campaign, popupCode, codeProp],
  )

  const ignoreConfirmation = useMemo(
    () =>
      getFirstDefinedElement([
        // The value from the props gets the highest priority so that the behaviour
        // of the `ignoreConfirmation` value matches that of `code`.
        ignoreConfirmationProp,
        // Fall back to the CampaignContext value for the majority of landing page
        // signup buttons.
        campaign?.ignoreConfirmation,
      ]),
    [campaign, ignoreConfirmationProp],
  )

  const href = useMemo(() => {
    const signupUrl = getSignupURL(`${locale}/signup`)
    return enhanceSignupUrl(signupUrl, reduxReferral, reduxName, ignoreConfirmation, code)
  }, [locale, reduxReferral, reduxName, ignoreConfirmation, code])

  return {
    href,
  }
}

function getFirstDefinedElement<T>(list: T[]): T | undefined {
  return list.find(v => v !== undefined)
}
